import type { AnimalDescriptor, AnimalKind } from "../game/animals";
import { type SimpleMessageKey, t } from "../i18n";
import type { ProfileSettings } from "../persistence";
import { formatVersionLabel, readVersionInfo } from "../version";

export interface StartScreenAudio {
  setEnabled(enabled: boolean): void;
  unlock(): Promise<void>;
}

export interface StartScreenOptions {
  readonly animals: readonly AnimalDescriptor[];
  readonly initialAnimal?: AnimalKind | null;
  readonly initialSettings: ProfileSettings;
  readonly audio?: StartScreenAudio;
  readonly onReplayTutorial?: () => void;
}

export interface StartChoice {
  readonly animal: AnimalKind;
  readonly settings: ProfileSettings;
}

const ANIMAL_LABEL_KEYS: Readonly<Record<AnimalKind, SimpleMessageKey>> = {
  seal: "animal.seal",
  penguin: "animal.penguin",
  fish: "animal.fish",
};

export class StartScreen {
  private readonly element: HTMLDivElement;
  private readonly parent: HTMLElement;
  private readonly animalButtons = new Map<AnimalKind, HTMLButtonElement>();
  private readonly disposers: (() => void)[] = [];
  private readonly playButton: HTMLButtonElement;
  private readonly audioToggle: HTMLButtonElement;
  private readonly audio: StartScreenAudio | null;
  private settings: ProfileSettings;
  private selectedAnimal: AnimalKind | null;
  private resolveSelection: ((choice: StartChoice) => void) | null = null;
  private readonly selectionPromise: Promise<StartChoice>;

  constructor(parent: HTMLElement, options: StartScreenOptions) {
    this.parent = parent;
    this.settings = options.initialSettings;
    this.selectedAnimal = options.initialAnimal ?? null;
    this.audio = options.audio ?? null;

    this.element = document.createElement("div");
    this.element.className = "waterra-start-screen";
    this.element.setAttribute("role", "dialog");
    this.element.setAttribute("aria-modal", "true");
    this.element.setAttribute("aria-label", t("ui.start.aria-label"));

    const content = document.createElement("div");
    content.className = "waterra-start-screen__content";
    this.element.appendChild(content);

    const title = document.createElement("h1");
    title.className = "waterra-start-screen__title";
    title.textContent = t("ui.start.title");
    content.appendChild(title);

    const prompt = document.createElement("p");
    prompt.className = "waterra-start-screen__prompt";
    prompt.textContent = t("ui.start.prompt");
    content.appendChild(prompt);

    const list = document.createElement("div");
    list.className = "waterra-start-screen__list";
    content.appendChild(list);

    for (const animal of options.animals) {
      const animalLabel = t(ANIMAL_LABEL_KEYS[animal.kind]);
      const button = document.createElement("button");
      button.type = "button";
      button.className = "waterra-start-screen__button";
      button.dataset.animal = animal.kind;
      button.textContent = animalLabel;
      button.setAttribute("aria-label", t("ui.start.play-as.aria", { animal: animalLabel }));
      const handler = (event: Event): void => {
        event.preventDefault();
        this.selectAnimal(animal.kind);
      };
      button.addEventListener("click", handler);
      this.disposers.push(() => {
        button.removeEventListener("click", handler);
      });
      this.animalButtons.set(animal.kind, button);
      list.appendChild(button);
    }

    const settingsPanel = document.createElement("div");
    settingsPanel.className = "waterra-start-screen__settings";

    const settingsLabel = document.createElement("h2");
    settingsLabel.className = "waterra-start-screen__settings-label";
    settingsLabel.textContent = t("ui.start.settings.heading");
    settingsPanel.appendChild(settingsLabel);

    this.audioToggle = document.createElement("button");
    this.audioToggle.type = "button";
    this.audioToggle.className = "waterra-start-screen__toggle";
    const toggleHandler = (event: Event): void => {
      event.preventDefault();
      this.settings = { ...this.settings, audio: !this.settings.audio };
      this.audio?.setEnabled(this.settings.audio);
      this.renderAudioToggle();
    };
    this.audioToggle.addEventListener("click", toggleHandler);
    this.disposers.push(() => {
      this.audioToggle.removeEventListener("click", toggleHandler);
    });
    settingsPanel.appendChild(this.audioToggle);

    if (options.onReplayTutorial) {
      const onReplay = options.onReplayTutorial;
      const replayButton = document.createElement("button");
      replayButton.type = "button";
      replayButton.className = "waterra-start-screen__replay-tutorial";
      replayButton.textContent = t("ui.start.tutorial.replay");
      const replayHandler = (event: Event): void => {
        event.preventDefault();
        replayButton.disabled = true;
        replayButton.classList.add("is-queued");
        replayButton.textContent = t("ui.start.tutorial.queued");
        onReplay();
      };
      replayButton.addEventListener("click", replayHandler);
      this.disposers.push(() => {
        replayButton.removeEventListener("click", replayHandler);
      });
      settingsPanel.appendChild(replayButton);
    }

    content.appendChild(settingsPanel);

    this.playButton = document.createElement("button");
    this.playButton.type = "button";
    this.playButton.className = "waterra-start-screen__play";
    this.playButton.textContent = t("ui.start.play");
    this.playButton.setAttribute("aria-label", t("ui.start.play.aria"));
    const playHandler = (event: Event): void => {
      event.preventDefault();
      this.confirm();
    };
    this.playButton.addEventListener("click", playHandler);
    this.disposers.push(() => {
      this.playButton.removeEventListener("click", playHandler);
    });
    content.appendChild(this.playButton);

    const versionText = formatVersionLabel(readVersionInfo());
    const version = document.createElement("p");
    version.className = "waterra-start-screen__version";
    version.textContent = versionText;
    version.setAttribute("aria-label", t("ui.start.version.aria", { label: versionText }));
    content.appendChild(version);

    this.selectionPromise = new Promise<StartChoice>((resolve) => {
      this.resolveSelection = resolve;
    });

    this.parent.appendChild(this.element);
    this.renderAnimalSelection();
    this.renderAudioToggle();
    this.renderPlayState();

    const initial = this.selectedAnimal;
    const focusTarget =
      (initial !== null ? this.animalButtons.get(initial) : null) ??
      this.animalButtons.values().next().value ??
      null;
    focusTarget?.focus();
  }

  selection(): Promise<StartChoice> {
    return this.selectionPromise;
  }

  dispose(): void {
    for (const cleanup of this.disposers) cleanup();
    this.disposers.length = 0;
    this.animalButtons.clear();
    this.element.remove();
  }

  private selectAnimal(kind: AnimalKind): void {
    this.selectedAnimal = kind;
    this.renderAnimalSelection();
    this.renderPlayState();
  }

  private confirm(): void {
    if (this.resolveSelection === null || this.selectedAnimal === null) return;
    void this.audio?.unlock();
    const resolve = this.resolveSelection;
    const choice: StartChoice = {
      animal: this.selectedAnimal,
      settings: this.settings,
    };
    this.resolveSelection = null;
    resolve(choice);
  }

  private renderAnimalSelection(): void {
    for (const [kind, button] of this.animalButtons) {
      const isSelected = kind === this.selectedAnimal;
      button.classList.toggle("is-selected", isSelected);
      button.setAttribute("aria-pressed", isSelected ? "true" : "false");
    }
  }

  private renderAudioToggle(): void {
    const enabled = this.settings.audio;
    this.audioToggle.textContent = enabled ? t("ui.start.sound.on") : t("ui.start.sound.off");
    this.audioToggle.setAttribute("aria-pressed", enabled ? "true" : "false");
    this.audioToggle.classList.toggle("is-on", enabled);
  }

  private renderPlayState(): void {
    const ready = this.selectedAnimal !== null;
    this.playButton.disabled = !ready;
    this.playButton.classList.toggle("is-ready", ready);
  }
}
